import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider');
  }
  return context;
};

// Safe version that returns defaults when ChatProvider is not present
export const useChatContextSafe = () => {
  const context = useContext(ChatContext);
  if (!context) {
    return { totalUnread: 0, lastReadTimestamps: {}, selectedCustomer: null, messages: [], setSelectedCustomer: () => {}, setMessages: () => {}, markAsRead: () => {}, calculateTotalUnread: () => {} };
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [totalUnread, setTotalUnread] = useState(0);
  const [lastReadTimestamps, setLastReadTimestamps] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [messages, setMessages] = useState([]);
  const { isAuthenticated, user } = useAuth();

  const isPlatformUser = () => {
    const category = String(user?.category || '').toLowerCase();
    const role = String(user?.role || '').toLowerCase();
    const userType = String(user?.userType || '').toLowerCase();
    return (
      category.includes('platform') ||
      role.includes('admin') ||
      role.includes('platform') ||
      userType === 'platform' ||
      userType === 'platform_user'
    );
  };

  // Track if we've been denied access to avoid spamming errors
  const [accessDenied, setAccessDenied] = useState(false);

  // Calculate total unread messages across all sessions using batch endpoint
  const calculateTotalUnread = async () => {
    try {
      if (!isAuthenticated || !localStorage.getItem('token') || !isPlatformUser() || accessDenied) {
        setTotalUnread(0);
        return;
      }

      // Build lastRead map from localStorage (message IDs, not timestamps)
      const lastReadKey = 'platformLastReadMessages';
      const lastReadMap = JSON.parse(localStorage.getItem(lastReadKey) || '{}');

      // Single batch request instead of N+1 queries
      const response = await api.get('/api/support/chat/unread-counts', {
        params: { lastRead: JSON.stringify(lastReadMap) }
      });

      if (response.data.success) {
        setTotalUnread(response.data.totalUnread || 0);
      }
    } catch (error) {
      // If we get a 403 or 500, stop polling to avoid spam
      if (error?.response?.status === 403 || error?.response?.status === 500) {
        setAccessDenied(true);
        setTotalUnread(0);
      }
    }
  };

  // Mark a session as read
  const markAsRead = (userId) => {
    setLastReadTimestamps(prev => ({ ...prev, [userId]: new Date() }));
  };

  // Reset access denied when user changes
  useEffect(() => {
    setAccessDenied(false);
  }, [user?.id, user?.email]);

  // Poll for unread count every 10 seconds
  useEffect(() => {
    if (!isAuthenticated || !localStorage.getItem('token') || !isPlatformUser() || accessDenied) {
      setTotalUnread(0);
      return;
    }
    calculateTotalUnread();
    const interval = setInterval(calculateTotalUnread, 10000);
    return () => clearInterval(interval);
  }, [lastReadTimestamps, isAuthenticated, accessDenied, user?.id, user?.email]);

  const value = useMemo(() => ({
    totalUnread,
    lastReadTimestamps,
    selectedCustomer,
    messages,
    setSelectedCustomer,
    setMessages,
    markAsRead,
    calculateTotalUnread
  }), [totalUnread, lastReadTimestamps, selectedCustomer, messages, markAsRead]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
